import type { IPhoto } from '../../common/index.js';

/**
 * Manages photo selection for photobook projects.
 *
 * Each call to `getNextBatch` returns the top-ranked photos still available,
 * ensuring the best-quality photos are distributed evenly across surfaces.
 * Unlike `CalendarPhotosSelector`, no face-count boost is applied — photobooks
 * prioritise overall photo quality without preferring group portraits.
 */
export class PhotobookPhotosSelector {

    private readonly remainingPhotos: IPhoto[];
    private readonly usedPhotos: Set<IPhoto>;
    private readonly photoMap: Map<string, IPhoto>;

    public constructor(photos: IPhoto[]) {
        this.remainingPhotos = [...photos];
        this.usedPhotos = new Set<IPhoto>();
        this.photoMap = new Map(photos.map((photo) => [photo.photoId, photo]));
    }

    /**
     * Returns the top `size` photos sorted by rank (descending) from the remaining pool.
     * The returned photos are not yet removed — call `updatedPhotoSet` after placement.
     */
    public getNextBatch(size: number): IPhoto[] {
        const sortedPhotos = this.remainingPhotos.sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0));

        return sortedPhotos.slice(0, size);
    }

    /** Removes a placed photo from the remaining pool and records it as used. */
    public updatedPhotoSet(photoId: string): void {
        const photo = this.photoMap.get(photoId);

        if (!photo) {
            return;
        }

        const index = this.remainingPhotos.findIndex((p) => p.photoId === photoId);

        if (index !== -1) {
            this.remainingPhotos.splice(index, 1);
            this.usedPhotos.add(photo);
        }
    }

    /** Returns all photos that were successfully placed across surfaces. */
    public getUsedPhotos(): IPhoto[] {
        return [...this.usedPhotos];
    }

}
